// A launched run's live progress and its code (TOM-120, ADR 0248 §6, §7;
// GET /v1/admin/evals/runs/:id/progress, specs/api_contracts.md §11.5). The
// server's own type is EvalRunProgress in cloud/src/core/neo/eval/launch.ts.
// Pure, so the run page's progress view says the same thing on every poll and
// is tested without a DOM (progress.test.ts).

import { formatDate } from '../format';
import { agentLabel, isExpertRole } from './agents';
import { formatUsd, shortSha } from './format';
import type { EvalLaunchDaily } from './launch';
import type {
  EvalLaunchedBy,
  EvalRole,
  EvalRunStatus,
  EvalShardCode,
  EvalStopReason,
} from './types';

export interface EvalRunProgress {
  runId: string;
  status: EvalRunStatus;
  stopReason: EvalStopReason | null;
  error: string | null;
  launchedBy: EvalLaunchedBy;
  createdAt: string;
  startedAt: string | null;
  lastProgressAt: string;
  finishedAt: string | null;
  budgetUsd: number | null;
  spend: {
    usd: number;
    modelUsd: number;
    judgeUsd: number;
    calls: number;
    errors: number;
    unpricedCalls: number;
  };
  daily: EvalLaunchDaily;
  models: {
    model: string;
    role: EvalRole;
    planned: number;
    written: number;
    complete: number;
    errored: number;
    costUsd: number;
    judgeCostUsd: number;
    gate: string | null;
  }[];
  shards: {
    total: number;
    queued: number;
    running: number;
    done: number;
    failed: number;
    skipped: number;
  };
  mixedCode: boolean;
  /** The builds the shards that started ran on; empty until one starts. */
  code: EvalShardCode[];
}

/** How often the run page reads a live run's progress. */
export const EVAL_PROGRESS_POLL_MS = 5_000;

const LIVE: readonly EvalRunStatus[] = ['queued', 'running', 'cancelling'];

/** A run still going: the run page shows its progress and polls it. */
export function isLiveRun(status: EvalRunStatus): boolean {
  return LIVE.includes(status);
}

/**
 * Whole percent done, never 100 until every planned result is in, and 0 for
 * nothing planned.
 */
export function percentDone(done: number, total: number): number {
  if (!Number.isFinite(done) || !Number.isFinite(total) || total <= 0 || done <= 0) return 0;
  if (done >= total) return 100;
  return Math.min(99, Math.floor((done / total) * 100));
}

/** Every model's results together: planned, written, graded and errored. */
export function progressTotals(p: Pick<EvalRunProgress, 'models'>): {
  planned: number;
  written: number;
  complete: number;
  errored: number;
} {
  return p.models.reduce(
    (t, m) => ({
      planned: t.planned + m.planned,
      written: t.written + m.written,
      complete: t.complete + m.complete,
      errored: t.errored + m.errored,
    }),
    { planned: 0, written: 0, complete: 0, errored: 0 }
  );
}

/** "$0.4200 of the $2.00 budget", or "$0.4200, no budget". */
export function spendText(p: Pick<EvalRunProgress, 'spend' | 'budgetUsd'>): string {
  return p.budgetUsd !== null
    ? `${formatUsd(p.spend.usd)} of the ${formatUsd(p.budgetUsd)} budget`
    : `${formatUsd(p.spend.usd)}, no budget`;
}

/** "The daily limit is off until …" while the cap is suspended (TOM-533); null while it applies. */
export function dailyOffText(d: Pick<EvalLaunchDaily, 'capUsd' | 'suspendedUntil'>): string | null {
  if (d.capUsd !== null) return null;
  return d.suspendedUntil === null
    ? 'The daily limit is off'
    : `The daily limit is off until ${formatDate(d.suspendedUntil)}`;
}

/** The daily cap in one line: spent, held back by runs still going, left; or that it is off. */
export function dailyText(d: EvalLaunchDaily): string {
  const off = dailyOffText(d);
  if (off !== null) return `${off} · ${formatUsd(d.spentUsd)} spent in the last 24 hours`;
  return (
    `${formatUsd(d.spentUsd)} spent of the ${formatUsd(d.capUsd)} daily cap in the last 24 hours` +
    (d.reservedUsd > 0 ? ` · ${formatUsd(d.reservedUsd)} held by runs still going` : '') +
    ` · ${formatUsd(d.leftUsd)} left`
  );
}

/** "2 of 5 shards done · 1 running · 2 queued", naming only the states that have shards. */
export function shardText(s: EvalRunProgress['shards']): string {
  if (s.total === 0) return 'No shards';
  const parts = [`${s.done} of ${s.total} ${s.total === 1 ? 'shard' : 'shards'} done`];
  if (s.running > 0) parts.push(`${s.running} running`);
  if (s.queued > 0) parts.push(`${s.queued} queued`);
  if (s.failed > 0) parts.push(`${s.failed} failed`);
  if (s.skipped > 0) parts.push(`${s.skipped} skipped`);
  return parts.join(' · ');
}

/** What the run is doing, in a sentence, for a live run; null once it has ended. */
export function liveStateText(
  p: Pick<EvalRunProgress, 'status' | 'stopReason' | 'launchedBy'>
): string | null {
  if (p.status === 'queued') {
    return p.stopReason === null
      ? 'Queued: waiting for the worker to claim its first shard.'
      : 'Stopping before it started.';
  }
  if (p.status === 'cancelling') {
    return 'Cancelling: the case in flight finishes and is graded, the shards still queued are skipped, and the run ends cancelled with what it finished.';
  }
  if (p.status !== 'running') return null;
  if (p.stopReason === 'budget') {
    return "Stopping: the run's budget is spent. The case in flight finishes, and the run ends budget exceeded with what it finished.";
  }
  if (p.stopReason === 'daily_cap') {
    return 'Stopping: the daily eval cap is spent. The case in flight finishes, and the run ends budget exceeded with what it finished.';
  }
  if (p.stopReason === 'cancel') return 'Cancelling.';
  return p.launchedBy === 'cli'
    ? 'Running in the CLI that launched it.'
    : 'Running on the worker, one shard at a time.';
}

/**
 * Whether the run page offers Cancel: a run the console or a schedule
 * launched, still queued or running, with no stop set. A CLI run has no stop
 * check (409 cli_run), so it is stopped where it runs.
 */
export function canCancel(
  p: Pick<EvalRunProgress, 'status' | 'stopReason' | 'launchedBy'>
): boolean {
  return (
    p.launchedBy !== 'cli' &&
    (p.status === 'queued' || p.status === 'running') &&
    p.stopReason === null
  );
}

const CANCEL_OUTCOME_TEXT: Record<
  'cancelling' | 'cancelled' | 'already_stopping' | 'already_finished',
  string
> = {
  cancelling:
    'Cancelling. The case in flight finishes and is graded, and the shards still queued are skipped.',
  cancelled: 'Cancelled. No shard had started, so nothing ran.',
  already_stopping: 'The run was already stopping. Nothing changed.',
  already_finished: 'The run had already finished. Nothing changed.',
};

export function cancelOutcomeText(
  outcome: 'cancelling' | 'cancelled' | 'already_stopping' | 'already_finished'
): string {
  return CANCEL_OUTCOME_TEXT[outcome];
}

/** Why a cancel failed, from the error body the route answered. */
export function cancelErrorText(body: { error?: string; message?: string }): string {
  switch (body.error) {
    case 'cli_run':
      return 'The CLI launched this run: stop it where it runs. Its process runs no stop check, so the console cannot cancel it.';
    case 'audit_unavailable':
      return 'The cancel could not be audited, so the run was not cancelled.';
    case 'not_found':
      return 'No such run.';
    default:
      return typeof body.message === 'string' && body.message.length > 0
        ? body.message
        : 'The cancel failed.';
  }
}

/** The label a run whose shards ran on different code carries (ADR 0248 §7). */
export const MIXED_CODE_LABEL = 'Mixed code, not comparable';

/**
 * "abc1234 (uncommitted changes) · Neo v20 · Watch w5 · 3 shards", and an
 * expert's own version when the build ran one ("Electrical (Sparky) el1+…",
 * TOM-533).
 */
export function buildText(c: EvalShardCode): string {
  const experts = Object.entries(c.promptVersions ?? {})
    .filter(([role]) => isExpertRole(role))
    .map(([role, version]) => `${agentLabel(role)} ${version}`);
  return [
    `${shortSha(c.gitSha)}${c.gitSha === null ? ' (unknown commit)' : ''}${c.gitDirty === true ? ' (uncommitted changes)' : ''}`,
    `Neo ${c.neoPromptVersion ?? '—'}`,
    `Watch ${c.watchPromptVersion ?? '—'}`,
    ...experts,
    `${c.shards} ${c.shards === 1 ? 'shard' : 'shards'}`,
  ].join(' · ');
}
